import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { IdCard, Plus, Loader2, Trash2, ShieldAlert, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "#a4133c";
const CATS = [
  { key: "clearance", label: "Clearances & Checks" },
  { key: "skill", label: "Skills & Training" },
  { key: "confidential", label: "Confidential Documents", simple: true },
];

export default function HrDocumentTypes() {
  const [types, setTypes] = useState(null);
  const load = () => api.hrDocTypes().then((d) => setTypes(d.types || [])).catch(() => { setTypes([]); toast.error("Could not load document types."); });
  useEffect(() => { load(); }, []);

  const patch = async (t, changes) => {
    setTypes((ts) => ts.map((x) => (x.id === t.id ? { ...x, ...changes } : x)));
    try {
      await api.hrDocTypeUpdate(t.id, { name: t.name, category: t.category, expiry_required: t.expiry_required, critical: t.critical, flag_if_expired: t.flag_if_expired, flag_if_missing: t.flag_if_missing, exclude_from_worker_reminders: t.exclude_from_worker_reminders ?? false, ...changes });
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed."); load(); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.name}"? Existing staff records keep their value, but it won't be selectable for new entries.`)) return;
    try { await api.hrDocTypeDelete(t.id); toast.success("Removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="hr-doctypes-page">
      <div className="flex items-center gap-2 mb-1">
        <IdCard className="h-5 w-5 text-slate-500" />
        <h1 className="text-xl font-semibold font-brand text-slate-800">HR Document Types</h1>
      </div>
      <p className="text-xs text-slate-500 mb-5">These drive the Document Type dropdowns on staff Clearances &amp; Checks and Skills &amp; Training. Mark whether an expiry date is required, whether the document is critical for rostering, and whether rostering should flag it when expired or missing.</p>

      {types === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        CATS.map((cat) => (
          <CategorySection key={cat.key} cat={cat}
            items={types.filter((t) => t.category === cat.key)}
            onPatch={patch} onRemove={remove} onAdded={load} />
        ))
      )}
    </div>
  );
}

function CategorySection({ cat, items, onPatch, onRemove, onAdded }) {
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState(true);
  const [critical, setCritical] = useState(false);
  const [flagExpired, setFlagExpired] = useState(true);
  const [flagMissing, setFlagMissing] = useState(false);
  const [excludeWorker, setExcludeWorker] = useState(false);
  const [busy, setBusy] = useState(false);
  const simple = !!cat.simple; // confidential: no expiry/critical relevance

  const add = async () => {
    if (!name.trim()) { toast.error("Enter a name."); return; }
    setBusy(true);
    try {
      await api.hrDocTypeCreate({ name: name.trim(), category: cat.key,
        expiry_required: simple ? false : expiry, critical: simple ? false : critical,
        flag_if_expired: simple ? false : flagExpired, flag_if_missing: simple ? false : flagMissing,
        exclude_from_worker_reminders: simple ? false : excludeWorker });
      setName(""); setExpiry(true); setCritical(false); setFlagExpired(true); setFlagMissing(false);
      toast.success("Added.");
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add."); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`doctype-cat-${cat.key}`}>
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/70 text-[11px] font-bold uppercase tracking-wider text-slate-500">{cat.label}
        {simple && <span className="ml-2 normal-case font-medium text-slate-400">— HR/Admin only, non-expiring, not used for rostering</span>}
      </div>

      {!simple && (
        <div className="hidden md:grid grid-cols-[1fr_100px_100px_100px_100px_110px_40px] gap-x-3 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          <div>Name</div><div className="text-center">Expiry required</div><div className="text-center">Critical (rostering)</div><div className="text-center">Flag if expired</div><div className="text-center">Flag if missing</div><div className="text-center">Skip worker emails</div><div></div>
        </div>
      )}

      {items.length === 0 && <div className="px-4 py-5 text-center text-xs text-slate-400">No document types yet — add one below.</div>}
      {items.map((t) => (
        <div key={t.id} className={`px-4 py-2.5 border-t border-slate-50 items-center ${simple ? "grid grid-cols-[1fr_auto] gap-2" : "flex flex-col gap-2 md:grid md:grid-cols-[1fr_100px_100px_100px_100px_110px_40px] md:gap-x-3"}`} data-testid={`doctype-row-${t.id}`}>
          <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
            {t.name}
            {!simple && t.critical && <ShieldAlert className="h-3.5 w-3.5 text-rose-500" title="Critical for rostering" />}
            {!simple && t.exclude_from_worker_reminders && <span className="text-[9px] bg-amber-100 text-amber-700 border border-amber-200 rounded px-1 py-0.5 font-semibold">No worker emails</span>}
          </div>
          {!simple && (
            <div className="flex flex-wrap gap-x-4 gap-y-2 md:contents">
              <div className="flex md:justify-center items-center gap-1.5">
                <Switch checked={!!t.expiry_required} onCheckedChange={(v) => onPatch(t, { expiry_required: v })} data-testid={`doctype-expiry-${t.id}`} />
                <span className="text-[10px] text-slate-400 md:hidden">Expiry required</span>
              </div>
              <div className="flex md:justify-center items-center gap-1.5">
                <Switch checked={!!t.critical} onCheckedChange={(v) => onPatch(t, { critical: v })} data-testid={`doctype-critical-${t.id}`} />
                <span className="text-[10px] text-slate-400 md:hidden">Critical</span>
              </div>
              <div className="flex md:justify-center items-center gap-1.5">
                <Switch checked={t.flag_if_expired !== false} onCheckedChange={(v) => onPatch(t, { flag_if_expired: v })} data-testid={`doctype-flagexpired-${t.id}`} />
                <span className="text-[10px] text-slate-400 md:hidden">Flag if expired</span>
              </div>
              <div className="flex md:justify-center items-center gap-1.5">
                <Switch checked={!!t.flag_if_missing} onCheckedChange={(v) => onPatch(t, { flag_if_missing: v })} data-testid={`doctype-flagmissing-${t.id}`} />
                <span className="text-[10px] text-slate-400 md:hidden">Flag if missing</span>
              </div>
              <div className="flex md:justify-center items-center gap-1.5">
                <Switch checked={!!t.exclude_from_worker_reminders} onCheckedChange={(v) => onPatch(t, { exclude_from_worker_reminders: v })} data-testid={`doctype-excludeworker-${t.id}`} />
                <span className="text-[10px] text-slate-400 md:hidden">Skip worker emails</span>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <button onClick={() => onRemove(t)} className="text-slate-300 hover:text-rose-600" data-testid={`doctype-remove-${t.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}

      <div className="border-t border-slate-100 bg-slate-50/40 px-4 py-3 flex flex-wrap items-center gap-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={`New ${cat.label.toLowerCase()} type…`} className="h-9 text-sm flex-1 min-w-[180px]" data-testid={`doctype-new-name-${cat.key}`} />
        {!simple && <label className="flex items-center gap-1.5 text-[11px] text-slate-600"><CalendarClock className="h-3.5 w-3.5 text-slate-400" /><Switch checked={expiry} onCheckedChange={setExpiry} data-testid={`doctype-new-expiry-${cat.key}`} />Expiry</label>}
        {!simple && <label className="flex items-center gap-1.5 text-[11px] text-slate-600"><ShieldAlert className="h-3.5 w-3.5 text-slate-400" /><Switch checked={critical} onCheckedChange={setCritical} data-testid={`doctype-new-critical-${cat.key}`} />Critical</label>}
        {!simple && <label className="flex items-center gap-1.5 text-[11px] text-slate-600"><Switch checked={flagExpired} onCheckedChange={setFlagExpired} data-testid={`doctype-new-flagexpired-${cat.key}`} />Flag expired</label>}
        {!simple && <label className="flex items-center gap-1.5 text-[11px] text-slate-600"><Switch checked={flagMissing} onCheckedChange={setFlagMissing} data-testid={`doctype-new-flagmissing-${cat.key}`} />Flag missing</label>}
        {!simple && <label className="flex items-center gap-1.5 text-[11px] text-slate-600"><Switch checked={excludeWorker} onCheckedChange={setExcludeWorker} data-testid={`doctype-new-excludeworker-${cat.key}`} />Skip worker emails</label>}
        <Button size="sm" onClick={add} disabled={busy} style={{ backgroundColor: CRIMSON }} className="h-9" data-testid={`doctype-add-${cat.key}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}Add
        </Button>
      </div>
    </div>
  );
}
